import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MessageSquare, Clock, Send, CheckCircle } from 'lucide-react';
import { Button, Input } from '../components/ui';
import { useToast } from '../components/ui/Toast';

const ContactPage = () => {
  const { showToast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    botField: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot bot check
    if (formData.botField) {
      showToast('Submission rejected.', 'error');
      return;
    }

    if (!formData.message.trim()) {
      showToast('Message cannot be empty.', 'error');
      return;
    }

    setIsSubmitting(true);

    // TODO: Implement actual contact form submission
    // For now, simulate a submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    showToast('Message sent! We\'ll get back to you soon.', 'success');
    setIsSubmitting(false);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      description: 'We\'ll respond within 24 hours',
      value: 'hello@calcbuildai.com',
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      description: 'Available for Pro members',
      value: 'Coming Soon',
    },
    {
      icon: Clock,
      title: 'Response Time',
      description: 'Free users: 24-48 hours',
      value: 'Pro users: Priority',
    },
  ];

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Message Sent - CalcBuildAI</title>
        </Helmet>
        
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Message Sent!
            </h1>
            <p className="text-gray-600 mb-8">
              Thanks for reaching out. We'll get back to you as soon as possible, 
              usually within 24 hours.
            </p>
            <Button onClick={() => setSubmitted(false)} variant="outline">
              Send Another Message
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us - CalcBuildAI</title>
        <meta name="description" content="Get in touch with the CalcBuildAI team. We're here to help!" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have a question, feedback, or need help? We're here for you.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Methods */}
            <div className="space-y-6">
              {contactMethods.map((method, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <method.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{method.title}</h3>
                      <p className="text-sm text-gray-500 mb-1">{method.description}</p>
                      <p className="text-blue-600 font-medium">{method.value}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* FAQ Link */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="font-semibold text-blue-900 mb-2">
                  Looking for answers?
                </h3>
                <p className="text-blue-700 text-sm mb-4">
                  Check our FAQ section for quick answers to common questions.
                </p>
                <a 
                  href="/pricing#faq" 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  View FAQ →
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Send us a message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Input
                      label="Your Name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      type="email"
                      label="Email Address"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <Input
                    label="Subject"
                    name="subject"
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    type="textarea"
                    label="Message"
                    name="message"
                    placeholder="Tell us more about your question or feedback..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                  />

                  {/* Honeypot field for basic bot defense */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="contact-company" className="sr-only">Company</label>
                    <input
                      id="contact-company"
                      name="botField"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.botField}
                      onChange={handleChange}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    loading={isSubmitting}
                    icon={Send}
                  >
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
